/**
 * <s-product-gallery> — PDP thumbnail carousel.
 *
 * The main track is a native scroll-snap container (swipe/trackpad work without JS).
 * This component wires thumbnails and prev/next arrows to the track and keeps the
 * active thumbnail (aria-current) in sync with the visible slide.
 *
 * Expected DOM (templates/product.liquid):
 *   [data-gallery-track] > [data-gallery-slide]...
 *   [data-gallery-prev] / [data-gallery-next]  (optional)
 *   [data-gallery-thumbs] [data-gallery-thumb="<index>"]  (optional)
 */
class SProductGallery extends HTMLElement {
  #ac;
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.slides = [...this.track.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.thumbList = this.querySelector('[data-gallery-thumbs]');
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.prev?.addEventListener('click', () => this.goTo(this.#index - 1), opts);
    this.next?.addEventListener('click', () => this.goTo(this.#index + 1), opts);
    this.thumbList?.addEventListener('click', this.#onThumbClick, opts);
    this.track.addEventListener('scroll', this.#onScroll, { passive: true, signal: this.#ac.signal });

    this.#update(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const offset = slide.offsetLeft - this.track.offsetLeft;
    this.track.scrollTo({ left: offset, behavior: this.#behavior() });
    this.#update(index);
  }

  #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #onThumbClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    this.goTo(Number(thumb.dataset.galleryThumb));
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (index !== this.#index) this.#update(index);
    });
  };

  #update(index) {
    const last = this.slides.length - 1;
    this.#index = Math.max(0, Math.min(index, last));

    this.thumbs.forEach((thumb, i) => {
      if (i === this.#index) {
        thumb.setAttribute('aria-current', 'true');
        const list = this.thumbList;
        if (list && list.scrollWidth > list.clientWidth) {
          const left = thumb.offsetLeft - list.offsetLeft - (list.clientWidth - thumb.offsetWidth) / 2;
          list.scrollTo({ left, behavior: this.#behavior() });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (this.prev) this.prev.disabled = this.#index <= 0;
    if (this.next) this.next.disabled = this.#index >= last;
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
